/**
 * イメージボードの手描きストロークの描画レイヤー。
 *
 * `World`（パン / ズームの transform がかかった要素）の中に置く。SVG 自体は
 * レイアウトを占有しない大きさにして `overflow: visible` で中身を見せ、
 * ワールド座標をそのまま SVG のユーザー座標として使う。これで拡大縮小は
 * 親の transform に任せられる。
 *
 * 画像アイテムより手前に出したいので z-index を上げるが、ポインタイベントは
 * 拾わない（描画中の入力は ImageBoard 側のオーバーレイが受ける）。
 */
import React, { memo, useMemo } from "react";
import styled from "styled-components";

import type { ImageBoardStroke, StrokePoint } from "../../app/services/imageBoard";
import { toWidthBands } from "./strokeGeometry";

/*
  width / height を 0 にしてはいけない。SVG では幅か高さが 0 の要素は
  「レンダリングを無効にする」と定められており、overflow: visible を付けても
  中身が一切描画されない（getBoundingClientRect は正しい値を返すため、
  レイアウトはできているのに見えない、という気付きにくい壊れ方をする）。
  レイアウトを占有させたくないので最小の 1px にしている。
*/
const Svg = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
  width: 1px;
  height: 1px;
  overflow: visible;
  pointer-events: none;
  /* 画像アイテム（既定の重なり）より手前に描く */
  z-index: 1;
`;

interface StrokePathProps {
  points: StrokePoint[];
  color: string;
  width: number;
  /** 消しゴムが触れていて、離せば消える状態 */
  fading?: boolean;
}

/**
 * 1 ストローク。筆圧の変化ぶんだけ太さの違うパスに分けて描く。
 * 点列が変わらない限り再計算しないよう memo 化する（他の人の
 * ストロークが増えても既存ぶんは描き直さない）。
 */
const StrokePath: React.FC<StrokePathProps> = memo(
  ({ points, color, width, fading }) => {
    const bands = useMemo(() => toWidthBands(points, width), [points, width]);
    return (
      <g opacity={fading ? 0.25 : 1}>
        {bands.map((band, index) => (
          <path
            key={index}
            d={band.d}
            stroke={color}
            strokeWidth={band.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        ))}
      </g>
    );
  },
);
StrokePath.displayName = "StrokePath";

/** 描画に使う項目だけ（共有ページの閲覧専用データは作成者を含まない） */
export type DrawableStroke = Pick<ImageBoardStroke, "id" | "points" | "color" | "width">;

interface BoardStrokeLayerProps {
  strokes: DrawableStroke[];
  /** 描いている最中のストローク（未保存）。 */
  livePoints: StrokePoint[] | null;
  liveColor: string;
  liveWidth: number;
  /** 消しゴムが触れていて、離したら消えるストロークの id。 */
  pendingEraseIds: ReadonlySet<string>;
}

const BoardStrokeLayer: React.FC<BoardStrokeLayerProps> = ({
  strokes,
  livePoints,
  liveColor,
  liveWidth,
  pendingEraseIds,
}) => (
  <Svg>
    {strokes.map((stroke) => (
      <StrokePath
        key={stroke.id}
        points={stroke.points}
        color={stroke.color}
        width={stroke.width}
        fading={pendingEraseIds.has(stroke.id)}
      />
    ))}
    {livePoints && livePoints.length > 0 && (
      <StrokePath points={livePoints} color={liveColor} width={liveWidth} />
    )}
  </Svg>
);

export default BoardStrokeLayer;
